<template>
  <div>
    <AppHeader />
    
    <!-- 主视觉区域 - 行业解决方案 -->
    <section class="relative h-96 flex items-center justify-center text-white bg-gradient-stone pt-16 overflow-hidden">
      <div class="absolute inset-0">
        <img :alt="$t('industrySolutions.hero.imageAlt')" class="w-full h-full object-cover opacity-40"
          src="/images/industrySolution/industrySolutions-head.jpg">
      </div>
      <div class="relative z-10 text-center px-4 max-w-4xl">
        <h1 class="text-5xl md:text-6xl font-extrabold leading-tight mb-4 animate-fade-in-up">
          {{ $t('industrySolutions.hero.title') }}
        </h1>
        <p class="text-xl md:text-2xl mb-6 max-w-3xl mx-auto animate-fade-in-up delay-200">
          {{ $t('industrySolutions.hero.subtitle') }}
        </p>
      </div>
    </section>

    <!-- 产品概览 -->
    <section class="py-20 px-6 md:px-12 bg-gray-100">
      <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
        {{ $t('industrySolutions.overview.title') }}
      </h2>
      <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
        <!-- 金融行业解决方案 -->
        <div class="industry-card">
          <img :alt="$t('industrySolutions.industries.finance.imageAlt')" class="w-full h-80 object-cover"
            src="/images/industrySolution/industry-finance.jpg">
          <div class="p-6">
            <h3 class="text-2xl font-bold mb-2 text-gray-900 flex items-center">
              <i class="fas fa-university text-blue-600 mr-3"></i>
              {{ $t('industrySolutions.industries.finance.title') }}
            </h3>
            <div class="mt-4 space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h4 class="font-semibold text-lg text-blue-700 mb-1">{{ $t('industrySolutions.industries.finance.painPoints.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.finance.painPoints.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-blue-700 mb-1">{{ $t('industrySolutions.industries.finance.solution.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.finance.solution.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-blue-700 mb-1">{{ $t('industrySolutions.industries.finance.results.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.finance.results.content') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 智能工单与AI分析平台 -->
        <div class="bg-white rounded-xl shadow-xl overflow-hidden">
          <img :alt="$t('industrySolutions.industries.ticketAi.imageAlt')" class="w-full h-80 object-cover"
            src="/images/industrySolution/industry-ticketAI.jpg">
          <div class="p-6">
            <h3 class="text-2xl font-bold mb-2 text-gray-900 flex items-center">
              <i class="fas fa-cogs text-purple-600 mr-3"></i>
              {{ $t('industrySolutions.industries.ticketAi.title') }}
            </h3>
            <div class="mt-4 space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h4 class="font-semibold text-lg text-purple-700 mb-1">{{ $t('industrySolutions.industries.ticketAi.ticketSystem.title') }}</h4>
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="(feature, index) in ticketSystemFeatures" :key="index">
                    {{ feature }}
                  </li>
                </ul>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-purple-700 mb-1">{{ $t('industrySolutions.industries.ticketAi.aiAnalytics.title') }}</h4>
                <ul class="list-disc pl-5 space-y-1">
                  <li v-for="(feature, index) in aiAnalyticsFeatures" :key="index">
                    {{ feature }}
                  </li>
                </ul>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-purple-700 mb-1">{{ $t('industrySolutions.industries.ticketAi.results.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.ticketAi.results.content') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 制造行业解决方案 -->
        <div class="industry-card">
          <img :alt="$t('industrySolutions.industries.manufacturing.imageAlt')" class="w-full h-80 object-cover"
            src="/images/industrySolution/industry-manufacturing.jpg">
          <div class="p-6">
            <h3 class="text-2xl font-bold mb-2 text-gray-900 flex items-center">
              <i class="fas fa-industry text-blue-800 mr-3"></i>
              {{ $t('industrySolutions.industries.manufacturing.title') }}
            </h3>
            <div class="mt-4 space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h4 class="font-semibold text-lg text-blue-800 mb-1">{{ $t('industrySolutions.industries.manufacturing.painPoints.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.manufacturing.painPoints.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-blue-800 mb-1">{{ $t('industrySolutions.industries.manufacturing.solution.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.manufacturing.solution.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-blue-800 mb-1">{{ $t('industrySolutions.industries.manufacturing.results.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.manufacturing.results.content') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 零售行业解决方案 -->
        <div class="industry-card">
          <img :alt="$t('industrySolutions.industries.retail.imageAlt')" class="w-full h-80 object-cover"
            src="/images/coreService/it-base.jpg">
          <div class="p-6">
            <h3 class="text-2xl font-bold mb-2 text-gray-900 flex items-center">
              <i class="fas fa-store text-orange-600 mr-3"></i>
              {{ $t('industrySolutions.industries.retail.title') }}
            </h3>
            <div class="mt-4 space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h4 class="font-semibold text-lg text-orange-700 mb-1">{{ $t('industrySolutions.industries.retail.painPoints.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.retail.painPoints.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-orange-700 mb-1">{{ $t('industrySolutions.industries.retail.solution.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.retail.solution.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-orange-700 mb-1">{{ $t('industrySolutions.industries.retail.results.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.retail.results.content') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 医疗行业解决方案 -->
        <div class="industry-card">
          <img :alt="$t('industrySolutions.industries.healthcare.imageAlt')" class="w-full h-80 object-cover"
            src="/images/industrySolution/industry-healthcare.jpg">
          <div class="p-6">
            <h3 class="text-2xl font-bold mb-2 text-gray-900 flex items-center">
              <i class="fas fa-heartbeat text-red-500 mr-3"></i>
              {{ $t('industrySolutions.industries.healthcare.title') }}
            </h3>
            <div class="mt-4 space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h4 class="font-semibold text-lg text-red-600 mb-1">{{ $t('industrySolutions.industries.healthcare.painPoints.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.healthcare.painPoints.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-red-600 mb-1">{{ $t('industrySolutions.industries.healthcare.solution.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.healthcare.solution.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-red-600 mb-1">{{ $t('industrySolutions.industries.healthcare.results.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.healthcare.results.content') }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- 政府与公共事业解决方案 -->
        <div class="industry-card">
          <img :alt="$t('industrySolutions.industries.government.imageAlt')" class="w-full h-80 object-cover"
            src="/images/industrySolution/industry-goverment.jpg">
          <div class="p-6">
            <h3 class="text-2xl font-bold mb-2 text-gray-900 flex items-center">
              <i class="fas fa-building text-gray-700 mr-3"></i>
              {{ $t('industrySolutions.industries.government.title') }}
            </h3>
            <div class="mt-4 space-y-4 text-gray-700 leading-relaxed">
              <div>
                <h4 class="font-semibold text-lg text-gray-700 mb-1">{{ $t('industrySolutions.industries.government.painPoints.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.government.painPoints.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-gray-700 mb-1">{{ $t('industrySolutions.industries.government.solution.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.government.solution.content') }}</p>
              </div>
              <div>
                <h4 class="font-semibold text-lg text-gray-700 mb-1">{{ $t('industrySolutions.industries.government.results.title') }}</h4>
                <p>{{ $t('industrySolutions.industries.government.results.content') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 重点行业方案 -->
    <section class="py-20 px-6 md:px-12 bg-gray-50">
      <div class="max-w-7xl mx-auto">
        <div class="max-w-4xl mx-auto text-center mb-14">
          <h2 class="text-4xl font-extrabold text-gray-900 mb-4">
            {{ $t('industrySolutions.focusIndustries.title') }}
          </h2>
          <p class="text-lg text-gray-600 leading-8">
            {{ $t('industrySolutions.focusIndustries.subtitle') }}
          </p>
        </div>

        <div class="focus-industry-grid">
          <article
            v-for="solution in focusIndustrySolutions"
            :key="solution.id"
            class="focus-industry-card rounded-3xl border border-gray-200 bg-white p-6 md:p-7 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            <div class="focus-industry-header">
              <div :class="['focus-industry-icon w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg bg-gradient-to-br', solution.gradient]">
                <i :class="[solution.icon, 'text-2xl']"></i>
              </div>
              <div class="min-w-0 flex-1">
                <p :class="['text-xs md:text-sm font-semibold tracking-[0.08em] mb-2', solution.iconColor]">
                  {{ $t('industrySolutions.focusIndustries.keySolutionLabel') }}
                </p>
                <h3 class="text-2xl font-bold text-gray-900 mb-2 leading-tight">
                  {{ solution.title }}
                </h3>
                <p class="text-base md:text-lg font-semibold text-gray-700 leading-7">
                  {{ solution.keySolution }}
                </p>
              </div>
            </div>

            <div class="focus-industry-summary">
              <div :class="['focus-industry-panel rounded-2xl border p-5', solution.surfaceClass]">
                <h4 class="text-xs font-semibold tracking-[0.08em] text-gray-500 mb-3">
                  {{ $t('industrySolutions.focusIndustries.painPointsTitle') }}
                </h4>
                <p class="text-gray-700 leading-7">
                  {{ solution.painPoints }}
                </p>
              </div>

              <div class="focus-industry-panel rounded-2xl border border-gray-200 bg-gray-50 p-5">
                <h4 class="text-xs font-semibold tracking-[0.08em] text-gray-500 mb-3">
                  {{ $t('industrySolutions.focusIndustries.outcomeTitle') }}
                </h4>
                <p class="text-gray-700 leading-7">
                  {{ solution.outcome }}
                </p>
              </div>
            </div>

            <div class="focus-industry-solutions">
              <h4 class="text-xs font-semibold tracking-[0.08em] text-gray-500 mb-4">
                {{ $t('industrySolutions.focusIndustries.solutionTitle') }}
              </h4>
              <ul class="space-y-3">
                <li
                  v-for="(point, index) in solution.solutions"
                  :key="`${solution.id}-solution-${index}`"
                  class="focus-industry-solution-item flex items-start gap-3 text-gray-700 leading-7"
                >
                  <i :class="['fas fa-check-circle mt-1 flex-shrink-0', solution.iconColor]"></i>
                  <span>{{ point }}</span>
                </li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- 扩展产品矩阵 -->
    <section id="product-capabilities" class="py-20 px-6 md:px-12 bg-white scroll-mt-24">
      <div class="max-w-7xl mx-auto">
        <div class="max-w-4xl mx-auto text-center mb-14">
          <h2 class="text-4xl font-extrabold text-gray-900 mb-4">
            {{ $t('industrySolutions.productMatrix.title') }}
          </h2>
          <p class="text-lg text-gray-600 leading-8">
            {{ $t('industrySolutions.productMatrix.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          <article
            v-for="product in productOfferings"
            :id="product.id"
            :key="product.id"
            class="rounded-3xl border border-gray-200 bg-gradient-to-br from-white via-white to-gray-50 p-7 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl"
          >
            <div class="flex items-start justify-between gap-4 mb-5">
              <div :class="['w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg bg-gradient-to-br', product.gradient]">
                <i :class="[product.icon, 'text-2xl']"></i>
              </div>
              <span :class="['text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap', product.badgeClass]">
                {{ product.tagline }}
              </span>
            </div>

            <h3 class="text-2xl font-bold text-gray-900 mb-3">
              {{ product.title }}
            </h3>
            <p class="text-gray-700 leading-7 mb-6">
              {{ product.description }}
            </p>

            <div class="space-y-5">
              <div>
                <h4 class="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">
                  {{ $t('industrySolutions.productMatrix.modulesTitle') }}
                </h4>
                <ul class="space-y-2">
                  <li
                    v-for="(module, index) in product.modules"
                    :key="`${product.id}-module-${index}`"
                    class="flex items-start text-gray-700"
                  >
                    <i class="fas fa-check-circle text-green-500 mt-1 mr-3"></i>
                    <span>{{ module }}</span>
                  </li>
                </ul>
              </div>

              <div>
                <h4 class="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500 mb-3">
                  {{ $t('industrySolutions.productMatrix.scenariosTitle') }}
                </h4>
                <div class="flex flex-wrap gap-2">
                  <span
                    v-for="(scenario, index) in product.scenarios"
                    :key="`${product.id}-scenario-${index}`"
                    :class="['px-3 py-2 rounded-full text-sm font-medium', product.scenarioClass]"
                  >
                    {{ scenario }}
                  </span>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="mt-12 rounded-3xl border border-gray-200 bg-gray-50 px-6 py-8 md:px-8">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div class="max-w-3xl">
              <h3 class="text-2xl font-bold text-gray-900 mb-3">
                {{ $t('industrySolutions.productMatrix.noteTitle') }}
              </h3>
              <p class="text-gray-700 leading-8">
                {{ $t('industrySolutions.productMatrix.note') }}
              </p>
            </div>
            <router-link
              to="/contact"
              class="inline-flex items-center justify-center bg-purple-600 text-white hover:bg-purple-700 px-6 py-3 rounded-2xl font-semibold shadow-lg transition duration-300 whitespace-nowrap"
            >
              {{ $t('industrySolutions.productMatrix.action') }}
              <i class="fas fa-arrow-right ml-3"></i>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- 核心功能特点 -->
    <section class="py-20 px-6 md:px-12 bg-gray-50">
      <h2 class="text-4xl font-extrabold text-center mb-14 text-gray-900">
        {{ $t('industrySolutions.coreFeatures.title') }}
      </h2>
      <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- 左侧功能 -->
        <div class="space-y-10">
          <!-- 智能工单管理 -->
          <div class="feature-item">
            <div class="feature-icon bg-purple-100">
              <i class="fas fa-ticket-alt text-purple-600 text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold mb-3 text-gray-900">{{ $t('industrySolutions.coreFeatures.ticketManagement.title') }}</h3>
              <p class="text-gray-700 leading-relaxed mb-4">
                {{ $t('industrySolutions.coreFeatures.ticketManagement.description') }}
              </p>
              <div class="feature-benefits">
                <div class="benefit-item" v-for="(benefit, index) in ticketManagementBenefits" :key="index">
                  <i class="fas fa-check-circle mr-2"></i>{{ benefit }}
                </div>
              </div>
            </div>
          </div>

          <!-- 实时数据分析 -->
          <div class="feature-item">
            <div class="feature-icon bg-blue-100">
              <i class="fas fa-chart-line text-blue-600 text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold mb-3 text-gray-900">{{ $t('industrySolutions.coreFeatures.dataAnalytics.title') }}</h3>
              <p class="text-gray-700 leading-relaxed mb-4">
                {{ $t('industrySolutions.coreFeatures.dataAnalytics.description') }}
              </p>
              <div class="feature-benefits">
                <div class="benefit-item" v-for="(benefit, index) in dataAnalyticsBenefits" :key="index">
                  <i class="fas fa-check-circle mr-2"></i>{{ benefit }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧功能 -->
        <div class="space-y-10">
          <!-- AI智能助手 -->
          <div class="feature-item">
            <div class="feature-icon bg-teal-100">
              <i class="fas fa-brain text-teal-600 text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold mb-3 text-gray-900">{{ $t('industrySolutions.coreFeatures.aiAssistant.title') }}</h3>
              <p class="text-gray-700 leading-relaxed mb-4">
                {{ $t('industrySolutions.coreFeatures.aiAssistant.description') }}
              </p>
              <div class="feature-benefits">
                <div class="benefit-item" v-for="(benefit, index) in aiAssistantBenefits" :key="index">
                  <i class="fas fa-check-circle mr-2"></i>{{ benefit }}
                </div>
              </div>
            </div>
          </div>

          <!-- 数据集成能力 -->
          <div class="feature-item">
            <div class="feature-icon bg-indigo-100">
              <i class="fas fa-plug text-indigo-600 text-2xl"></i>
            </div>
            <div>
              <h3 class="text-2xl font-bold mb-3 text-gray-900">{{ $t('industrySolutions.coreFeatures.dataIntegration.title') }}</h3>
              <p class="text-gray-700 leading-relaxed mb-4">
                {{ $t('industrySolutions.coreFeatures.dataIntegration.description') }}
              </p>
              <div class="feature-benefits">
                <div class="benefit-item" v-for="(benefit, index) in dataIntegrationBenefits" :key="index">
                  <i class="fas fa-check-circle mr-2"></i>{{ benefit }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA区域 -->
    <section class="py-20 px-6 md:px-12 bg-gradient-stone text-white">
      <div class="max-w-5xl mx-auto text-center">
        <h2 class="text-4xl font-extrabold mb-8">
          {{ $t('industrySolutions.cta.title') }}
        </h2>
        <p class="text-xl leading-relaxed mb-12 opacity-90">
          {{ $t('industrySolutions.cta.description') }}
        </p>
        <router-link 
          to="/contact"
          class="inline-block bg-white text-purple-700 hover:bg-purple-100 px-8 py-4 rounded-full text-lg font-semibold shadow-lg transition duration-300 transform hover:scale-105">
          {{ $t('industrySolutions.cta.button') }}
          <i class="fas fa-envelope ml-2"></i>
        </router-link>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';

const { tm } = useI18n();

// 使用 tm() 函数获取原始消息对象（支持数组）
// 获取核心功能的benefits数组
const ticketManagementBenefits = computed(() => tm('industrySolutions.coreFeatures.ticketManagement.benefits'));
const dataAnalyticsBenefits = computed(() => tm('industrySolutions.coreFeatures.dataAnalytics.benefits'));
const aiAssistantBenefits = computed(() => tm('industrySolutions.coreFeatures.aiAssistant.benefits'));
const dataIntegrationBenefits = computed(() => tm('industrySolutions.coreFeatures.dataIntegration.benefits'));

// 获取工单系统和AI分析的features数组
const ticketSystemFeatures = computed(() => tm('industrySolutions.industries.ticketAi.ticketSystem.features'));
const aiAnalyticsFeatures = computed(() => tm('industrySolutions.industries.ticketAi.aiAnalytics.features'));

const focusIndustryDecorations = [
  {
    id: 'manufacturing',
    icon: 'fas fa-industry',
    gradient: 'from-cyan-500 to-sky-600',
    iconColor: 'text-cyan-600',
    surfaceClass: 'bg-cyan-50 border-cyan-100'
  },
  {
    id: 'finance',
    icon: 'fas fa-landmark',
    gradient: 'from-blue-600 to-indigo-700',
    iconColor: 'text-blue-600',
    surfaceClass: 'bg-blue-50 border-blue-100'
  },
  {
    id: 'retail',
    icon: 'fas fa-store',
    gradient: 'from-amber-500 to-orange-600',
    iconColor: 'text-orange-600',
    surfaceClass: 'bg-orange-50 border-orange-100'
  },
  {
    id: 'healthcare',
    icon: 'fas fa-heartbeat',
    gradient: 'from-rose-500 to-red-600',
    iconColor: 'text-rose-600',
    surfaceClass: 'bg-rose-50 border-rose-100'
  },
  {
    id: 'government',
    icon: 'fas fa-building-columns',
    gradient: 'from-slate-600 to-gray-800',
    iconColor: 'text-slate-600',
    surfaceClass: 'bg-slate-50 border-slate-200'
  },
  {
    id: 'ticket-ai',
    icon: 'fas fa-cogs',
    gradient: 'from-violet-500 to-purple-700',
    iconColor: 'text-violet-600',
    surfaceClass: 'bg-violet-50 border-violet-100'
  }
];

const focusIndustrySolutions = computed(() => tm('industrySolutions.focusIndustries.items').map((item, index) => ({
  ...item,
  ...focusIndustryDecorations[index]
})));

const productOfferingDecorations = [
  {
    id: 'crm-suite',
    icon: 'fas fa-address-book',
    gradient: 'from-sky-500 to-blue-600',
    badgeClass: 'bg-sky-50 text-sky-700',
    scenarioClass: 'bg-sky-50 text-sky-700'
  },
  {
    id: 'marketing-automation',
    icon: 'fas fa-bullseye',
    gradient: 'from-emerald-500 to-teal-600',
    badgeClass: 'bg-emerald-50 text-emerald-700',
    scenarioClass: 'bg-emerald-50 text-emerald-700'
  },
  {
    id: 'mini-program',
    icon: 'fas fa-mobile-screen-button',
    gradient: 'from-orange-500 to-amber-500',
    badgeClass: 'bg-orange-50 text-orange-700',
    scenarioClass: 'bg-orange-50 text-orange-700'
  },
  {
    id: 'ai-knowledge-base',
    icon: 'fas fa-book-open-reader',
    gradient: 'from-violet-500 to-fuchsia-600',
    badgeClass: 'bg-violet-50 text-violet-700',
    scenarioClass: 'bg-violet-50 text-violet-700'
  },
  {
    id: 'office-agent',
    icon: 'fas fa-robot',
    gradient: 'from-slate-600 to-slate-800',
    badgeClass: 'bg-slate-100 text-slate-700',
    scenarioClass: 'bg-slate-100 text-slate-700'
  }
];

const productOfferings = computed(() => tm('industrySolutions.productMatrix.items').map((item, index) => ({
  ...item,
  ...productOfferingDecorations[index]
})));
</script>

<style scoped>
@import '@/assets/css/main.css';
@import '@/assets/css/industrySolutions.css';
</style>
